import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { assign } from 'lodash'
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core'
import FormItem from 'components/FormItem'
import { isBlank } from 'common/usual'
import {
  experimentDetailPromise,
  experimentCreatePromise,
  experimentUpdatePromise,
  experimentVariantCreatePromise,
  experimentVariantUpdatePromise,
  experimentAssignmentListPromise,
  PLATFORMS,
} from 'api/Experiment'

export default function ExperimentUpsert (props) {
  const id = props.match.params.id
  const [experiment, setExperiment] = useState({ active: true, variants: [] })
  const [assignments, setAssignments] = useState({})
  const [btnStatus, setBtnStatus] = useState(false)
  const [variantDraft, setVariantDraft] = useState({})

  const disableBtn = useMemo(() => btnStatus || isBlank(experiment.name), [btnStatus, experiment.name])

  const getDetail = useCallback(async () => {
    if (!id) return
    const detail = await experimentDetailPromise(id)
    setExperiment(detail || {})

    // The counts live on the assignments route, not on detail: they are assignment data rather
    // than configuration, and a weight change made without the existing split visible is blind.
    const assigned = await experimentAssignmentListPromise(id, { size: 1 })
    setAssignments(assigned || {})
  }, [id])

  useEffect(() => { getDetail() }, [getDetail])

  const handleChange = useCallback((...values) => {
    setExperiment(current => assign({}, current, ...values))
  }, [])

  const commonProps = useMemo(() => ({
    onChange: handleChange,
    data: experiment
  }), [handleChange, experiment])

  function handleSubmit () {
    setBtnStatus(true)

    const body = {
      name: experiment.name,
      countryId: isBlank(experiment.countryId) ? null : Number(experiment.countryId),
      startBmi: isBlank(experiment.startBmi) ? null : experiment.startBmi,
      platform: isBlank(experiment.platform) ? null : Number(experiment.platform),
      newUserOnly: !!experiment.newUserOnly,
      active: !!experiment.active,
    }

    // `updatedAt` goes on update only -- it is the optimistic-concurrency token the API requires
    // back, and the create schema rejects it as an unknown key.
    const request = id
      ? experimentUpdatePromise(id, { ...body, updatedAt: experiment.updatedAt })
      : experimentCreatePromise(body)

    request
      .then(resp => {
        // Straight into the variants table: an experiment with no variants assigns nobody.
        if (!id && resp && resp.id) return props.history.replace(`/experiments/edit/${resp.id}`)
        return props.history.goBack()
      })
      // 409 = it changed under us. Refetch so the form carries a fresh token; resubmitting the old
      // one would fail identically forever.
      .catch(err => { if (err && err.status === 409) return getDetail() })
      .finally(() => setBtnStatus(false))
  }

  function handleVariantChange (key, payload) {
    setVariantDraft(current => ({ ...current, [key]: { ...(current[key] || {}), ...payload } }))
  }

  function variantBody (draft) {
    return {
      variantName: draft.variantName,
      primarySku: isBlank(draft.primarySku) ? null : Number(draft.primarySku),
      secondarySku: isBlank(draft.secondarySku) ? null : Number(draft.secondarySku),
      weight: isBlank(draft.weight) ? undefined : Number(draft.weight),
      active: !!draft.active,
    }
  }

  function handleVariantSave (variant) {
    const draft = { ...variant, ...(variantDraft[variant.id] || {}) }

    setBtnStatus(true)
    experimentVariantUpdatePromise(variant.id, { ...variantBody(draft), updatedAt: variant.updatedAt })
      .then(() => getDetail())
      .catch(err => { if (err && err.status === 409) return getDetail() })
      .finally(() => setBtnStatus(false))
  }

  function handleVariantAdd () {
    const draft = variantDraft.new || {}
    if (isBlank(draft.variantId)) return

    setBtnStatus(true)
    experimentVariantCreatePromise(id, { ...variantBody(draft), variantId: Number(draft.variantId) })
      .then(() => { setVariantDraft(current => ({ ...current, new: {} })); return getDetail() })
      // the draft stays put on failure: the row is what the admin has to correct
      .catch(() => {})
      .finally(() => setBtnStatus(false))
  }

  const byVariant = assignments.byVariant || {}

  return (
    <div className={'root'}>
      <Card className={'form-container'}>
        <FormItem label={'Name'} name={'name'} isRequired {...commonProps} />
        <FormItem label={'Country ID'} name={'countryId'} type={'number'} {...commonProps} />
        <FormItem label={'Start BMI'} name={'startBmi'} placeholder={'low / med / high'} {...commonProps} />

        <FormItem label={'Platform'} name={'platform'} title={'Matched against the user os_type. Empty means every platform.'}>
          <TextField
            select
            SelectProps={{ native: true }}
            value={isBlank(experiment.platform) ? '' : experiment.platform}
            onChange={event => handleChange({ platform: event.target.value })}>
            <option value=''>every platform</option>
            {PLATFORMS.map(platform => (
              <option key={platform.value} value={platform.value}>{platform.label} ({platform.value})</option>
            ))}
          </TextField>
        </FormItem>

        <FormItem label={'New User Only'} name={'newUserOnly'}>
          <Switch checked={Boolean(experiment.newUserOnly)} onChange={event => handleChange({ newUserOnly: event.target.checked })} />
        </FormItem>
        <FormItem label={'Active'} name={'active'}>
          <Switch checked={Boolean(experiment.active)} onChange={event => handleChange({ active: event.target.checked })} />
        </FormItem>

        <Button
          className='save-btn'
          color='primary'
          fullWidth
          size='large'
          type='submit'
          variant='contained'
          onClick={handleSubmit}
          disabled={disableBtn}>
          Save
        </Button>
      </Card>

      { id &&
        <Card style={{ marginTop: 16 }}>
          <CardHeader
            title='Variants'
            subheader={`${assignments.count || 0} users assigned. A weight change only moves users assigned from now on -- including a weight of 0, which stops new assignments and leaves the existing arm running. Rewriting assignments mid-experiment would invalidate the result.`}
          />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Variant ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Primary SKU</TableCell>
                  <TableCell>Secondary SKU</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell title='Users assigned to this variant ID. Two rows sharing one variant ID share the count.'>Assigned (per variant ID)</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(experiment.variants || []).map(variant => {
                  const draft = { ...variant, ...(variantDraft[variant.id] || {}) }
                  return (
                    <TableRow key={variant.id} hover>
                      <TableCell>{variant.variantId}</TableCell>
                      <TableCell>
                        <TextField size='small' value={draft.variantName || ''} onChange={event => handleVariantChange(variant.id, { variantName: event.target.value })} />
                      </TableCell>
                      <TableCell>
                        <TextField size='small' value={isBlank(draft.primarySku) ? '' : draft.primarySku} onChange={event => handleVariantChange(variant.id, { primarySku: event.target.value })} />
                      </TableCell>
                      <TableCell>
                        <TextField size='small' value={isBlank(draft.secondarySku) ? '' : draft.secondarySku} onChange={event => handleVariantChange(variant.id, { secondarySku: event.target.value })} />
                      </TableCell>
                      <TableCell>
                        <TextField size='small' value={isBlank(draft.weight) ? '' : draft.weight} onChange={event => handleVariantChange(variant.id, { weight: event.target.value })} />
                      </TableCell>
                      <TableCell>{byVariant[variant.variantId] || 0}</TableCell>
                      <TableCell>
                        <Switch checked={Boolean(draft.active)} onChange={event => handleVariantChange(variant.id, { active: event.target.checked })} />
                      </TableCell>
                      <TableCell>
                        <Button size='small' color='primary' disabled={btnStatus} onClick={() => handleVariantSave(variant)}>Save</Button>
                      </TableCell>
                    </TableRow>
                  )
                })}

                <TableRow>
                  <TableCell>
                    <TextField size='small' placeholder='id' value={(variantDraft.new || {}).variantId || ''} onChange={event => handleVariantChange('new', { variantId: event.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField size='small' placeholder='name' value={(variantDraft.new || {}).variantName || ''} onChange={event => handleVariantChange('new', { variantName: event.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField size='small' value={(variantDraft.new || {}).primarySku || ''} onChange={event => handleVariantChange('new', { primarySku: event.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField size='small' value={(variantDraft.new || {}).secondarySku || ''} onChange={event => handleVariantChange('new', { secondarySku: event.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField size='small' value={(variantDraft.new || {}).weight || ''} onChange={event => handleVariantChange('new', { weight: event.target.value })} />
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>
                    <Switch checked={Boolean((variantDraft.new || {}).active)} onChange={event => handleVariantChange('new', { active: event.target.checked })} />
                  </TableCell>
                  <TableCell>
                    <Button size='small' color='primary' disabled={btnStatus} onClick={handleVariantAdd}>Add</Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <p className='tips'>
              The SKUs are product ids. The API rejects one with no active product, because
              getUpgradeSKU loads them through the active scope -- a bad id would simply drop that
              product from the paywall without an error anywhere.
            </p>
          </CardContent>
        </Card>
      }
    </div>
  )
}
